import * as Sentry from '@sentry/react';
import submitConchBatch from 'consumers/httpSubmitBatch';
import { compressParams } from 'containers/RequestReport/utils';
import Alert from 'react-s-alert';
import {
  change,
  getFormValues,
  reset,
  startSubmit,
  stopSubmit,
} from 'redux-form';
import { put, select, take, call } from 'redux-saga/effects';
import selectGgEnvironment from 'selectors/ggEnvironment';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

import errorAction from '../actions/error';
import { FORM_SUBMIT } from '../actions/formSubmit';
import { REQUEST_REPORT_FORM } from '../constants/formNames';
import graphqlClient from '../consumers/graphqlClient';
import ViewerQuery from '../graphql/queries/ViewerQuery';
import {
  exceededRequestReportQuota,
  getJobQueue,
  makeReportParams,
} from '../utils/conchUtils';
import { hasSuperAdminRole } from '../utils/roleUtils';

export default function* requestReportFormSubmitSaga() {
  while (true) {
    yield take(
      ({ type, payload }) =>
        type === FORM_SUBMIT && payload === REQUEST_REPORT_FORM,
    );
    let isSubmitAndStay = false;
    yield put(startSubmit(REQUEST_REPORT_FORM));
    const formValues = yield select(getFormValues(REQUEST_REPORT_FORM));
    isSubmitAndStay = formValues.submitAndStay === true;
    try {
      const viewerInfoResponse = yield graphqlClient.query({
        query: ViewerQuery,
      });
      const viewerInfoResult = viewerInfoResponse.data;
      const viewerInfoData = viewerInfoResult.viewer;

      const workspaceProducerId = yield select(selectWorkspaceProducerId);
      const userRoles = yield select(selectTokenRoles);
      const ggEnvironment = yield select(selectGgEnvironment);

      if (!hasSuperAdminRole(userRoles)) {
        const hasExceededQuota = yield call(
          exceededRequestReportQuota,
          viewerInfoData.id,
          workspaceProducerId,
        );
        if (hasExceededQuota) {
          yield put(change(REQUEST_REPORT_FORM, 'submitFailed', true));
          yield put(change(REQUEST_REPORT_FORM, 'limitExceeded', true));
          yield put(stopSubmit(REQUEST_REPORT_FORM));
          return;
        }
      }

      const report_params:
        | conch.MarketSurveyReportParams
        | conch.OptimizationReportParams
        | conch.UpdateReportParams = makeReportParams(
          formValues,
          viewerInfoData,
        );

      const jobName = [
        report_params.client_name,
        report_params.project_name,
        report_params.target_group_name,
      ]
        .map((d) => d.replace(/ /g, '-'))
        .join('_');

      const report_params_zipped = compressParams(JSON.stringify(report_params))

      // Submit request to conch batch service
      const response: Response = yield call(
        submitConchBatch,
        ggEnvironment,
        jobName,
        report_params.job_definition,
        getJobQueue(report_params.numCores),
        report_params,
        report_params_zipped,
      );

      if (!response.ok) {
        // Log the error response to Sentry for analysis
        const error = new Error(`Request Report form submission failed: ${response.statusText}`,);
        Sentry.captureException(error, {
          contexts: {
            response: {
              status: response.status,
              statusText: response.statusText,
              url: response.url,
            },
          },
        });
        yield put(stopSubmit(REQUEST_REPORT_FORM, { _error: response.statusText }));
        yield put(change(REQUEST_REPORT_FORM, 'submitFailed', true));
        if (!isSubmitAndStay) {
          yield put(
            errorAction({
              error: response.statusText,
              title: 'Failed to Request Report',
              description: 'Something went wrong while submitting your report.',
            }),
          );
        }
      } else {
        yield put(stopSubmit(REQUEST_REPORT_FORM));
        if (!isSubmitAndStay) {
          yield put(reset(REQUEST_REPORT_FORM));
          yield put(
            change(REQUEST_REPORT_FORM, 'submittedType', formValues.report_type),
          );
          yield put(change(REQUEST_REPORT_FORM, 'submitSucceeded', true));
          yield put(
            change(
              REQUEST_REPORT_FORM,
              'submittedProjectName',
              report_params.project_name,
            ),
          );
        }
      }

      // Submit & Stay: show toast and reset flags so user can resubmit
      if (isSubmitAndStay) {
        if (response.ok) {
          Alert.success(`${report_params.project_name} Submission Successful`, {
            position: 'top-right',
            effect: 'slide',
            beep: false,
            timeout: 4000,
          });
        } else {
          Alert.error('Request Submission Failed. Try again or contact product support.', {
            position: 'top-right',
            effect: 'slide',
            beep: false,
            timeout: 4000,
          });
        }
        yield put(change(REQUEST_REPORT_FORM, 'submitFailed', false));
        yield put(change(REQUEST_REPORT_FORM, 'submitAndStay', false));
      }
    } catch (error) {
      yield Sentry.captureException(error);
      yield put(stopSubmit(REQUEST_REPORT_FORM, error));
      yield put(change(REQUEST_REPORT_FORM, 'submitFailed', true));
      if (!isSubmitAndStay) {
        yield put(
          errorAction({
            error,
            title: 'Failed to Request Report',
            description: error.message,
          }),
        );
      } else {
        Alert.error('Request Submission Failed. Try again or contact product support.', {
          position: 'top-right',
          effect: 'slide',
          beep: false,
          timeout: 4000,
        });
        yield put(change(REQUEST_REPORT_FORM, 'submitFailed', false));
        yield put(change(REQUEST_REPORT_FORM, 'submitAndStay', false));
      }
    }
  }
}
